import ProductCount from './product-count';
import { addClass, getComponent, getComponentPart, getPopup, removeClass } from './common';
import InputHint from './input-hint';
import LoadingIndication from './loading-indication';

export default class OrderMatrix {
    baseElement: HTMLElement;
    addToCartButton: HTMLElement;
    enterCalculationButton: HTMLElement;
    limitToStockAmount: boolean;
    private validationMessage: string;
    onSubmit: (productCounts: ProductCount[]) => void;
    onEnterCalculation: (productCounts: ProductCount[]) => void;
    inputHints: InputHint[] = [];

    static componentName = 'OrderMatrix';
    private static readonly buyButtonPartName = 'buy-button';
    private static readonly enterCalculationButtonPartName = 'enter-calculation-button';

    constructor(
        parentElement: HTMLElement,
        onSubmit: (productCounts: ProductCount[]) => void,
        onEnterCalculation: (productCounts: ProductCount[]) => void = null
    ) {

        this.setQuintessence();

        this.baseElement = getComponent(parentElement, OrderMatrix.componentName);
        this.addToCartButton = getComponentPart(this.baseElement, OrderMatrix.buyButtonPartName);
        this.enterCalculationButton = getComponentPart(this.baseElement, OrderMatrix.enterCalculationButtonPartName);
        this.limitToStockAmount = this.baseElement.dataset.limitToStockAmount && this.baseElement.dataset.limitToStockAmount === 'true';

        this.onSubmit = onSubmit;
        this.onEnterCalculation = onEnterCalculation;
        this.init();
    }

    private setQuintessence() {        

        var quintessence = document.querySelector(".quintessence");
        if (quintessence) {
            var container = document.querySelector(".mfp-container");
            container.classList.add("quintEssenceBlur");

            var btnClsoe = document.querySelector("button.mfp-close");
            btnClsoe.addEventListener('click', event => {
                container.classList.remove("quintEssenceBlur");
            });

            var popup = document.querySelector(".white-popup");
            popup.classList.add("quintessence-popup");
            popup.classList.remove("small");
        }
    }

    private init(): void {

        if (this.addToCartButton && this.onSubmit) {
            this.addToCartButton.addEventListener('click', async event => {
                await this.submitProductCounts(event, this.onSubmit);
            });
        }

        if (this.enterCalculationButton && this.onEnterCalculation) {
            this.enterCalculationButton.addEventListener('click', async event => {
                await this.submitProductCounts(event, this.onEnterCalculation);
            });
        }

        this.initInputs();
        this.validateInputs();
    }

    private isTotalCountPositive(): boolean {
        const counts = this.collectProductCounts();
        return counts && counts.length > 0 && counts.map(x => x.quantity).reduce((x, y) => x + y) > 0;
    }

    private async submitProductCounts(event: Event, action: (productCounts: ProductCount[]) => void): Promise<void> {
        event.preventDefault();
        if (!this.validateInputs()) {
            return;
        }

        this.showLoadingIndication();
        await action(this.collectProductCounts());
    }

    private collectProductCounts(): ProductCount[] {
        const elements: Element[] = [
            ...this.baseElement.querySelectorAll('input[type="text"]')
        ];
        const inputs = elements.map(input => input as HTMLInputElement);
        const filteredInputs = inputs.filter(input => input.value);
        const productCounts = filteredInputs.map(
            input =>
                new ProductCount(
                    Number(input.dataset.productId),
                    input.dataset.productGuid,
                    Number(input.value)
                )
        );
        return productCounts;
    }

    private initInputs() {
        const inputs = this.getQuantityInputs();
        inputs.forEach(input => {
            input.addEventListener('change', evt => this.quantityUpdated(evt));
            input.addEventListener('keyup', evt => this.quantityUpdated(evt));
            input.addEventListener('blur', evt => this.inputBlur(evt));
        });
    }

    private quantityUpdated(evt: Event): void {
        this.showHint(evt.target as HTMLInputElement);
        this.validateInputs();
    }

    private inputBlur(evt: Event): void {
        this.hideHint(evt.target as HTMLInputElement);
    }

    private showHint(input: HTMLInputElement) {
        const className = this.validateInput(input) ? 'valid' : 'invalid';
        const message = this.validationMessage;
        if (!message || message == null) return;
        const existingHint = this.inputHints[input.dataset.productGuid];
        const hint = existingHint ?? new InputHint(input);
        this.inputHints[input.dataset.productGuid] = hint;
        hint.show(message, className);
    }

    private hideHint(input: HTMLInputElement): void {
        const hint: InputHint = this.inputHints[input.dataset.productGuid];
        if (!hint) return;
        hint.hide();
    }

    private validateInputs(): boolean {
        const inputs = this.getQuantityInputs();
        let ok = true;
        inputs.forEach(input => {
            const result = this.validateInput(input as HTMLInputElement);
            ok = ok && result;
        });

        ok = ok && this.isTotalCountPositive();

        ok ? this.enableButtons() : this.disableButtons();

        return ok;
    }

    private validateInput(input: HTMLInputElement): boolean {
        if (input.value === "") {
            this.unmark(input);
            return true;
        }
        const value = parseInt(input.value);

        if (value === 0) {
            input.value = "";
            return false;
        }

        if (!value) {
            if (value === 0) {
                this.unmark(input)
                return true;
            } else {
                this.markAsInvalid(input);
                return false;
            }
        }

        if (input.dataset.minimumCount != null
            && input.dataset.minimumCount != ""
            && value < parseInt(input.dataset.minimumCount)) {
            this.markAsInvalid(input);
            this.validationMessage = input.dataset.minimumCountMessage;
            return false;
        }

        if (input.dataset.maximumCount != null
            && input.dataset.maximumCount != ""
            && value > parseInt(input.dataset.maximumCount)) {
            this.markAsInvalid(input);
            this.validationMessage = input.dataset.maximumCountMessage;
            return false;
        }

        if (input.dataset.multiplyCount != null
            && input.dataset.multiplyCount != ""
            && value % parseInt(input.dataset.multiplyCount) != 0) {
            this.markAsInvalid(input);
            this.validationMessage = input.dataset.multiplyCountMessage;
            return false;
        }

        const max = parseInt(input.dataset.stock);
        if (max && value > max || value < 0) {
            this.markAsInvalid(input);
            this.validationMessage = input.dataset.stockMessage;
            if (this.limitToStockAmount) {
                return false;
            } else {
                return true;
            }
        }
        this.markAsValid(input);
        return true;
    }

    private enableButtons() {
        removeClass(this.addToCartButton, 'disabled');
        this.enterCalculationButton && removeClass(this.enterCalculationButton, 'disabled');
    }

    private disableButtons() {
        addClass(this.addToCartButton, 'disabled');
        this.enterCalculationButton && addClass(this.enterCalculationButton, 'disabled');
    }

    private getQuantityInputs(): HTMLInputElement[] {
        return [...this.baseElement.querySelectorAll('input[data-component-part="quantity-input"]')].map(x => x as HTMLInputElement);
    }

    private markAsInvalid(input: HTMLInputElement) {
        removeClass(input, 'valid');
        addClass(input, 'invalid');
    }

    private markAsValid(input: HTMLInputElement) {
        addClass(input, 'valid');
        removeClass(input, 'invalid');
    }

    private unmark(input: HTMLInputElement) {
        removeClass(input, 'invalid');
        removeClass(input, 'valid');
    }

    private showLoadingIndication(): void {
        LoadingIndication.show(this.baseElement);
    }
}
